import { useRef, ChangeEvent, MouseEvent, useState } from "react";
import { FormattedMessage } from "react-intl";

import { ReactComponent as UploadIcon } from "assets/images/iconsV2/upload-cloud-02.svg";
import { ReactComponent as PDFIcon } from "assets/images/iconsV2/pdf.svg";
import { ReactComponent as AlertIcon } from "assets/images/iconsV2/alert-circle.svg";
import { ReactComponent as TrashIcon } from "assets/images/iconsV2/trash-01.svg";
import Button from "components/v2/Button/Button";
import LoadingSpinner from "components/v2/LoadingSpinner/LoadingSpinner";
import { formatFileSize } from "helpers/commonHelpers";
import useDraggableUpload from "hooks/useDraggableUpload";
import { FormattedUploadedFile } from "types/Common";
import "./UploadDropzone.scss";

export interface UploadDropzoneProps {
  onFileSelected?: (files: File[]) => void;
  uploadedFile?: FormattedUploadedFile | null;
  accept?: string;
  disabled?: boolean;
  onRemoveFile?: () => void;
  isUploading: boolean;
  isDeleting: boolean;
}

export default function UploadDropzone({
  onFileSelected,
  uploadedFile,
  accept = "application/pdf",
  disabled = false,
  onRemoveFile,
  isUploading,
  isDeleting,
}: UploadDropzoneProps) {
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const isUploadingOrDeleting = isUploading || isDeleting;
  const hasFile = !!uploadedFile;
  const uploadBlocked = disabled || hasFile || isUploadingOrDeleting;

  const openFileDialog = () => {
    if (uploadBlocked) return;
    inputRef.current?.click();
  };

  const handleFiles = (files: FileList | null) => {
    setError(null);
    if (uploadBlocked) return;
    if (!files || !onFileSelected) return;

    const filesArray = Array.from(files);
    const uploadedFile = files[0];

    if (uploadedFile.type !== "application/pdf") {
      return;
    }

    if (uploadedFile.size > 15_000_000) {
      setError("File size exceeds 15 MB limit.");
      return;
    }

    onFileSelected(filesArray);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  const handleRemoveClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation(); // don't trigger openFileDialog
    if (disabled) return;
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    onRemoveFile?.();
  };

  const { dragging, ...draggableProps } = useDraggableUpload(handleFiles);

  const rootClassName = [
    "upload-dropzone",
    !hasFile ? "upload-dropzone--empty" : "",
    error ? "upload-dropzone--error" : "",
    dragging ? "upload-dropzone--dragging" : "",
    disabled ? "upload-dropzone--disabled" : "",
  ]
    .filter(Boolean)
    .join(" ");

  function UploadedFile() {
    return (
      <div className="upload-dropzone__file-preview">
        <div className="d-flex align-items-center gap-lg">
          <PDFIcon />

          <div>
            <div className="text-secondary-v2 font-medium text-sm">{uploadedFile!.fileName}</div>
            <div className="text-tertiary font-regular text-xs">
              {isUploading ? (
                <>
                  <FormattedMessage id="general.uploading" defaultMessage="Uploading" />
                  ...
                </>
              ) : isDeleting ? (
                <>
                  <FormattedMessage id="general.deleting" defaultMessage="Deleting" />
                  ...
                </>
              ) : (
                formatFileSize(uploadedFile!.size)
              )}
            </div>
          </div>
        </div>

        {isUploadingOrDeleting ? (
          <LoadingSpinner size="24" />
        ) : (
          <Button
            iconLeft={<TrashIcon />}
            onClick={handleRemoveClick}
            variant="link-error"
            size="sm"
            disabled={isUploadingOrDeleting}
          />
        )}
      </div>
    );
  }

  function Icon() {
    return (
      <div className={`upload-dropzone__icon-container ${error ? "upload-dropzone__icon-container--error" : ""}`}>
        {error ? <AlertIcon /> : <UploadIcon />}
      </div>
    );
  }

  return (
    <>
      {hasFile && uploadedFile && <UploadedFile />}

      {!hasFile && (
        <div
          className={rootClassName}
          onClick={openFileDialog}
          {...draggableProps}
          tabIndex={uploadBlocked ? -1 : 0}
          aria-disabled={uploadBlocked}
        >
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple={false}
            onChange={handleChange}
            className="upload-dropzone__input"
            disabled={uploadBlocked}
          />

          <div className="upload-dropzone__inner">
            <Icon />
            {error ? (
              <div className="upload-dropzone__text">
                <div className="upload-dropzone__title">{error}</div>
                <div className="upload-dropzone__subtitle">Please reupload a valid PDF file.</div>
              </div>
            ) : (
              <>
                <p className="upload-dropzone__title">
                  <span className="upload-dropzone__title--primary">
                    <FormattedMessage
                      id="documentParser.uploadPage.uploadDropzone.clickToUpload"
                      defaultMessage="Click to upload"
                    />
                  </span>{" "}
                  <FormattedMessage
                    id="documentParser.uploadPage.uploadDropzone.orDragAndDrop"
                    defaultMessage=" or drag and drop"
                  />
                </p>
                <p className="upload-dropzone__subtitle">
                  <FormattedMessage
                    id="documentParser.uploadPage.uploadDropzone.PDFonly"
                    defaultMessage="PDF only (max. 15MB)"
                  />
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

UploadDropzone.defaultProps = {
  onFileSelected: () => {},
  uploadedFile: null,
  accept: "application/pdf",
  disabled: false,
  onRemoveFile: () => {},
};
